import { hexLen, hexToWorld, type Hex } from '../hex.ts';
import { idiv, isqrt } from '../math/fixed.ts';
import { deriveSeed, rngInt, seedRng } from '../math/rng.ts';

/**
 * The seventh altar (docs/sendas/paladin.md, chapter 5): two boards, the same for every seed but for what the seed picks.
 *
 * - The yard (act 1, the opening): the bastion's yard at night, the keep to the north (−y) with the fall's rubble heaped
 *   at its foot over the stair, holes in the old flagstones the garrison's skeletons come up, Ser Varo and his two
 *   knights to the south watching with their arms crossed.
 * - The cistern (acts 3 and 4, the campfire and the speech): Somnhold's old cistern under the keep, a round basin with
 *   the island of the seventh altar in its middle, its dry stone rim all round, four gates on the rim at the corners (the
 *   yard south-west, the barracks north-west, the forge north-east, the ravine south-east) and the stair coming down at
 *   the south. Its floor rises from the island's foot to the basin's lip: at mark L the water reaches out to `wetR(L)`;
 *   above five it stands against the basin's wall, the island under it above four. In act 3 the basin is deep (nobody
 *   walks into it); in act 4 its water is a ring at the ankles, and the floor walkable. Salt runnels run across its floor
 *   between the gates.
 *
 * Integers only. The seed picks the gate Varo's knights hold (act 4).
 */

export const YARD_RADIUS = 11;
/** The keep's block (y from its south face north), half its width; the stair's mouth at its foot. */
export const KEEP_S = -3900;
export const KEEP_HALF = 3000;
export const STAIR_AT = { x: 0, y: -3300 };

export const CIST_RADIUS = 11;
/** The island, the basin's lip, the rim's outer wall, the stair's half width (it comes down from the south). */
export const ISLAND_R = 1600;
export const BASIN_R = 6200;
export const RIM_R = 8700;
export const CIST_STAIR_HALF = 950;
/** Where the gates stand on the rim (their distance from the middle). */
export const GATE_R = 6900;

/** How far the water reaches out at mark `level` (0: none; five or more: to the basin's lip). */
export const wetR = (level: number): number => (level <= 0 ? ISLAND_R : level >= 5 ? BASIN_R : ISLAND_R + idiv((BASIN_R - ISLAND_R) * level, 5));

/** The quarter a point is in, by the gate that feeds it: 0 the yard (SW), 1 the barracks (NW), 2 the forge (NE), 3 the ravine (SE). */
export const quarterOf = (x: number, y: number): number => (x < 0 ? (y >= 0 ? 0 : 1) : y < 0 ? 2 : 3);

/** A point's distance from the middle. */
export const radiusOf = (x: number, y: number): number => isqrt(x * x + y * y);

/** Whether a point of the yard is floor (the keep's block is not). */
export const yardFloor = (x: number, y: number): boolean => !(y <= KEEP_S && Math.abs(x) <= KEEP_HALF);

/** Whether a point of the cistern is floor: the rim (and the stair's foot); the basin too when `deep` is off. */
export const cisternFloor = (x: number, y: number, deep: boolean): boolean => {
  const r = radiusOf(x, y);
  if (r < BASIN_R) return !deep;
  if (r <= RIM_R) return true;
  return y > 0 && Math.abs(x) <= CIST_STAIR_HALF;
};

export interface YardLayout {
  rocks: Hex[];
  piles: { x: number; y: number }[];
  holes: { x: number; y: number }[];
  keeps: Record<string, { x: number; y: number }>;
  start: { x: number; y: number };
}

export interface CisternLayout {
  /** The rocks of each act's board: the deep basin (act 3), the ankle-deep ring (act 4). */
  deep: Hex[];
  shallow: Hex[];
  /** The gates: the yard, the barracks, the forge, the ravine. */
  gates: { x: number; y: number }[];
  /** Salt runnels across the basin's floor, between the gates (x0, y0, x1, y1). */
  runnels: number[][];
  /** The gate Varo's knights hold in the fight. */
  held: number;
  keeps: Record<string, { x: number; y: number }>;
  start: { well: { x: number; y: number }; fled: { x: number; y: number } };
}

const rocksOf = (R: number, floor: (x: number, y: number) => boolean): Hex[] => {
  const rocks: Hex[] = [];
  for (let r = -R - 2; r <= R + 2; r++)
    for (let q = -R - 2; q <= R + 2; q++) {
      const d = hexLen(q, r);
      if (d > R + 2) continue;
      const w = hexToWorld(q, r);
      if (d > R || !floor(w.x, w.y)) rocks.push({ q, r });
    }
  return rocks;
};

let YARD: YardLayout | null = null;

/** The yard (the same for every seed). */
export const yardLayout = (): YardLayout => {
  if (YARD) return YARD;
  YARD = {
    rocks: rocksOf(YARD_RADIUS, yardFloor),
    // Heaped over the stair's mouth and along the keep's foot: the stair's own two first, the rest out from it.
    piles: [
      { x: -700, y: -3100 },
      { x: 700, y: -3100 },
      { x: -2300, y: -2500 },
      { x: 2300, y: -2500 },
      { x: -1300, y: -1500 },
      { x: 1300, y: -1500 },
    ],
    // The old drains in the flagstones, by the keep's corners and the yard's sides.
    holes: [
      { x: -4600, y: -4300 },
      { x: 4600, y: -4300 },
      { x: -6300, y: -900 },
      { x: 6300, y: -900 },
    ],
    keeps: {
      marit: { x: -600, y: 600 },
      nuno: { x: -1400, y: 1100 },
      varo: { x: 0, y: 5600 },
      orderKnight: { x: -1300, y: 5900 },
      orderKnight2: { x: 1300, y: 5900 },
    },
    start: { x: 400, y: 900 },
  };
  return YARD;
};

/** The runnels: eight spokes across the basin's floor, between the gates' quarters' middles and their edges. */
const spokes = (): number[][] => {
  // Unit directions at 0°, 45°, … in thousandths (cos, sin).
  const D: [number, number][] = [
    [1000, 0],
    [707, 707],
    [0, 1000],
    [-707, 707],
    [-1000, 0],
    [-707, -707],
    [0, -1000],
    [707, -707],
  ];
  // (Not where the stair comes down, nor under the gates: the ones at 45° are a hex in from the lip.)
  return D.map(([c, s], i) => {
    const r0 = 2500;
    const r1 = i % 2 ? 4800 : 5700;
    return [idiv(c * r0, 1000), idiv(s * r0, 1000), idiv(c * r1, 1000), idiv(s * r1, 1000)];
  });
};

const CIST = new Map<number, CisternLayout>();

/** The cistern for a fight's seed. */
export const cisternLayout = (seed: number): CisternLayout => {
  const hit = CIST.get(seed);
  if (hit) return hit;
  const rng = seedRng(deriveSeed(seed, 0x7e47));
  const g = idiv(GATE_R * 707, 1000);
  const L: CisternLayout = {
    deep: rocksOf(CIST_RADIUS, (x, y) => cisternFloor(x, y, true)),
    shallow: rocksOf(CIST_RADIUS, (x, y) => cisternFloor(x, y, false)),
    gates: [
      { x: -g, y: g },
      { x: -g, y: -g },
      { x: g, y: -g },
      { x: g, y: g },
    ],
    runnels: spokes(),
    // The knights hold one of the far three (the yard's is by the stair, where Marit's people stand).
    held: 1 + rngInt(rng, 3),
    keeps: {
      // Act 3: Marit at the stair's foot; act 4: kneeling at the altar (sworn together), or four hexes from it.
      marit: { x: 0, y: 7700 },
      maritAltar: { x: 0, y: 700 },
      maritAway: { x: 0, y: 4000 },
      nuno: { x: -800, y: 7800 },
      salinera: { x: -2600, y: 7300 },
      salter: { x: 2600, y: 7300 },
      varo: { x: 0, y: 9300 },
      varoIn: { x: -1100, y: 3400 },
    },
    start: { well: { x: 900, y: 7600 }, fled: { x: 700, y: 2300 } },
  };
  if (CIST.size > 64) CIST.clear();
  CIST.set(seed, L);
  return L;
};
